import { Card } from "@/components/ui/Card";
import { ErrorMessage } from "@/components/ui/ErrorMessage";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { useAchievements } from "@/hooks/use-achievements";
import type { AchievementDefinition } from "@/types";
import { getErrorMessage } from "@/utils/error-message";

const categoryLabels: Record<AchievementDefinition["category"], string> = {
  milestone: "Milestones",
  mastery: "Category Mastery",
  difficulty: "Difficulty",
  special: "Special",
};

const categoryOrder: AchievementDefinition["category"][] = ["milestone", "mastery", "difficulty", "special"];

export function Achievements() {
  const { data, isLoading, error } = useAchievements();

  const groupUnlocked = new Map<string, (AchievementDefinition & { unlockedAt: string })[]>();
  const groupLocked = new Map<string, AchievementDefinition[]>();

  if (data) {
    for (const a of data.unlocked) {
      const list = groupUnlocked.get(a.category) ?? [];
      list.push(a);
      groupUnlocked.set(a.category, list);
    }
    for (const a of data.locked) {
      const list = groupLocked.get(a.category) ?? [];
      list.push(a);
      groupLocked.set(a.category, list);
    }
  }

  const total = data ? data.unlocked.length + data.locked.length : 0;
  const earned = data?.unlocked.length ?? 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-navy-700">Achievements</h1>
        {data && (
          <span className="text-sm font-medium text-gray-500">{earned}/{total} unlocked</span>
        )}
      </div>

      {isLoading && <LoadingSpinner message="Loading achievements..." />}
      {error && <ErrorMessage message={getErrorMessage(error)} />}

      {data && earned > 0 && (
        <div className="flex items-center gap-2">
          <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-gray-100">
            <div
              className="h-full rounded-full bg-amber-400 transition-all"
              style={{ width: `${Math.round((earned / total) * 100)}%` }}
            />
          </div>
          <span className="text-xs font-bold text-amber-600">{Math.round((earned / total) * 100)}%</span>
        </div>
      )}

      {data && categoryOrder.map((cat) => {
        const unlocked = groupUnlocked.get(cat) ?? [];
        const locked = groupLocked.get(cat) ?? [];
        if (unlocked.length === 0 && locked.length === 0) return null;

        return (
          <div key={cat}>
            <h2 className="mb-3 text-sm font-medium uppercase tracking-wider text-gray-500">
              {categoryLabels[cat]}
            </h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {unlocked.map((a) => (
                <Card key={a.id} className="flex items-center gap-3 border-2 border-amber-200 bg-amber-50/50">
                  <span className="text-3xl">{a.icon}</span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-gray-900">{a.name}</p>
                    <p className="text-xs text-gray-500">{a.description}</p>
                    <p className="mt-0.5 text-xs text-amber-600">
                      Earned {new Date(a.unlockedAt).toLocaleDateString()}
                    </p>
                  </div>
                </Card>
              ))}
              {locked.map((a) => (
                <Card key={a.id} className="flex items-center gap-3 opacity-50">
                  <span className="text-3xl grayscale">{a.icon}</span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-gray-500">{a.name}</p>
                    <p className="text-xs text-gray-400">{a.description}</p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        );
      })}

      {data && earned === 0 && (
        <Card className="text-center">
          <p className="text-gray-500">Start playing to earn your first achievement!</p>
        </Card>
      )}
    </div>
  );
}
